import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Select2 } from '@/components/forms/Select2';
import { Select2Async } from '@/components/forms/Select2Async';
import { DateRangePicker } from '@/components/forms/DateRangePicker';
import { StatWidget } from '@/components/widgets/StatWidget';
import { FilterBar, type FilterField } from '@/components/common/FilterBar';
import { DataGrid, type DataGridColumn } from '@/components/tables/DataGrid';
import { formatNumber, formatDateTime } from '@/utils/format';
import { filterDataPaged } from '@/api/services/filterData';
import { executionQualityApi } from '@/api/services/executionQuality';
import { todayDateString } from '@/api/services/riskDashboard';
import type { DealSlippageDto, ExecutionQualityFilterDto } from '@/api/types/lpAccounts';

const CCY: Record<string, string> = { USD: '$', EUR: '€', GBP: '£', CHF: '₣', JPY: '¥' };
const SLIPPAGE_TYPES = [
  { value: '0', label: 'Positive' },
  { value: '1', label: 'Negative' },
];
const n = (v: number | null | undefined) => (v == null || Number.isNaN(v) ? '0.00' : formatNumber(v, 2));
const price = (v: number | null | undefined, digits?: number | null) => (v == null ? '' : formatNumber(v, digits ?? 5));
const dt = (v: string | null | undefined) => formatDateTime(v);

interface EqFilters {
  startDate: string;
  endDate: string;
  dataSourceId: number | null;
  symbol: string | null;
  dealId: number | null;
  slippageType: string | null;
}

export function ExecutionQualityTab() {
  const today = todayDateString();
  const initial: EqFilters = { startDate: today, endDate: today, dataSourceId: null, symbol: null, dealId: null, slippageType: null };
  const [draft, setDraft] = useState<EqFilters>(initial);
  const [applied, setApplied] = useState<EqFilters>(initial);
  // Bumped per Fetch (only for today-ending data) so re-applying identical filters forces
  // a fresh request; static historical results stay cached. react-query keys by value.
  // fetchPulse gives the Fetch button a brief spinner even on a cache hit.
  const [fetchNonce, setFetchNonce] = useState(0);
  const [fetchPulse, setFetchPulse] = useState(false);

  const connectors = useQuery({ queryKey: ['eq-connectors'], queryFn: executionQualityApi.getConnectors, staleTime: 5 * 60_000 });

  const toFilter = (f: EqFilters): ExecutionQualityFilterDto => ({
    startDate: f.startDate,
    endDate: f.endDate,
    dataSourceId: f.dataSourceId,
    symbol: f.symbol,
    dealId: f.dealId,
    slippageType: f.slippageType != null ? Number(f.slippageType) : null,
  });

  // Auto-fetches on load (legacy index.js fetches on document ready).
  const q = useQuery({
    queryKey: ['eq-data', applied, fetchNonce],
    queryFn: () => executionQualityApi.getSlippageData(toFilter(applied)),
  });
  const r = q.data;
  const sym = CCY[r?.reportingCurrency ?? ''] ?? (r?.reportingCurrency ? `${r.reportingCurrency} ` : '');
  const money = (v: number | null | undefined) => (v == null || Number.isNaN(v) ? 'N/A' : `${sym}${formatNumber(v, 2)}`);

  const rows = useMemo(() => [...(r?.dealSlippages ?? [])].sort((a, b) => (b.time > a.time ? 1 : -1)), [r]);

  const str = (v: string | number | null) => (v == null ? '' : String(v));

  const fields: (React.ReactNode | FilterField)[] = [
    {
      node: (
        <div key="date">
          <label className="form-label">Date Range</label>
          <DateRangePicker mode="range" maxDate={today} value={{ start: draft.startDate, end: draft.endDate }} onChange={(v) => setDraft((f) => ({ ...f, startDate: v.start > v.end ? v.end : v.start, endDate: v.end }))} />
        </div>
      ),
      pinned: true,
      width: 270,
    },
    <Select2 key="conn" label="Connector" allowClear placeholder="All" value={str(draft.dataSourceId)} options={connectors.data ?? []} onChange={(v) => setDraft((f) => ({ ...f, dataSourceId: v ? Number(v) : null }))} />,
    <div key="sym">
      <label className="form-label">Instrument</label>
      <Select2Async placeholder="All" value={str(draft.symbol) || null} fetch={filterDataPaged.instruments} onChange={(v) => setDraft((f) => ({ ...f, symbol: v || null }))} />
    </div>,
    <Select2 key="st" label="Slippage Type" allowClear placeholder="All" value={str(draft.slippageType)} options={SLIPPAGE_TYPES} onChange={(v) => setDraft((f) => ({ ...f, slippageType: v || null }))} />,
    <div key="deal">
      <label className="form-label">Deal ID</label>
      <input className="form-control" inputMode="numeric" value={draft.dealId ?? ''} onChange={(e) => setDraft((f) => ({ ...f, dealId: e.target.value ? Number(e.target.value) : null }))} />
    </div>,
  ];

  const widgets: { label: string; value: string; icon: string; theme: 'neutral' | 'primary' | 'success' | 'danger' | 'warning' }[] = [
    { label: 'Total Slippage', value: money(r?.totalSlippage), icon: 'ri-exchange-dollar-line', theme: 'warning' },
    { label: 'Total Positive', value: money(r?.totalPositive), icon: 'ri-arrow-up-line', theme: 'success' },
    { label: 'Total Negative', value: money(r?.totalNegative), icon: 'ri-arrow-down-line', theme: 'danger' },
    { label: 'Best Symbol', value: r?.bestSymbol || 'N/A', icon: 'ri-medal-line', theme: 'success' },
    { label: 'Worst Symbol', value: r?.worstSymbol || 'N/A', icon: 'ri-error-warning-line', theme: 'danger' },
  ];

  const columns: DataGridColumn<DealSlippageDto>[] = [
    { key: 'login', title: 'Login', value: (r) => r.login },
    { key: 'dealId', title: 'Deal', value: (r) => r.dealId },
    { key: 'orderId', title: 'Order', value: (r) => r.orderId },
    { key: 'symbol', title: 'Instrument', value: (r) => r.symbol },
    { key: 'time', title: 'Time', value: (r) => r.time, cell: (r) => dt(r.time) },
    { key: 'volume', title: 'Volume', className: 'text-end', value: (r) => r.volume, cell: (r) => n(r.volume) },
    { key: 'dealEntry', title: 'Entry', value: (r) => r.dealEntry },
    { key: 'dealType', title: 'Type', value: (r) => r.dealType },
    { key: 'dealPrice', title: 'Deal Price', className: 'text-end', value: (r) => r.dealPrice, cell: (r) => price(r.dealPrice, r.digits) },
    { key: 'orderPrice', title: 'Order Price', className: 'text-end', value: (r) => r.orderPrice ?? null, cell: (r) => price(r.orderPrice, r.digits) },
    { key: 'accountCurrency', title: 'Account Currency', value: (r) => r.accountCurrency },
    { key: 'accountCurrencyValue', title: 'Slippage Value AC', className: 'text-end', value: (r) => r.accountCurrencyValue ?? null, cell: (r) => n(r.accountCurrencyValue) },
    { key: 'reportingCurrencyValue', title: 'Slippage Value RC', className: 'text-end', value: (r) => r.reportingCurrencyValue ?? null, cell: (r) => n(r.reportingCurrencyValue) },
    { key: 'dataSourceName', title: 'Platform', value: (r) => r.dataSourceName },
  ];

  const fetch = () => {
    setApplied({ ...draft });
    if (draft.endDate === today) setFetchNonce((n) => n + 1);
    setFetchPulse(true);
    window.setTimeout(() => setFetchPulse(false), 450);
  };

  return (
    <>
      <FilterBar
        card={false}
        fields={fields}
        actions={
          <button type="button" className="btn btn-primary btn-sm" onClick={fetch} disabled={q.isFetching || fetchPulse}>
            {q.isFetching || fetchPulse ? <i className="fa fa-spinner fa-spin me-1" /> : <i className="fa fa-search me-1" />}
            Fetch
          </button>
        }
      />

      <div className="row g-2 mb-2 border-top pt-2 mt-2">
        {widgets.map((w) => (
          <div className="col-xl col-md-4 col-sm-6" key={w.label}>
            <StatWidget label={w.label} value={w.value} icon={w.icon} theme={w.theme} />
          </div>
        ))}
      </div>

      <DataGrid
        columns={columns}
        data={rows}
        rowKey={(r) => `${r.dealId}:${r.orderId}`}
        pageSize={5}
        card={false}
        includeExcel
        excelFileName="execution-quality"
        emptyMessage={q.isFetching ? 'Loading…' : q.isError ? <span className="text-danger">Failed to fetch execution quality data.</span> : 'No data for the current filters.'}
      />
    </>
  );
}
